import { gql } from '@apollo/client';

/**
 * Provisions the selected signed PandaDoc documents into a group: for each
 * document the backend creates a user (if missing), a student, an agreement
 * and a single contribution. Documents are processed independently — an error
 * on one does not cancel the rest.
 */
export const ProvisionStudents = gql`
  mutation ProvisionPandaDocStudents(
    $groupId: ID!
    $contributionAmount: Float!
    $currency: String!
    $documents: [ProvisionDocOverrideInput!]!
  ) {
    provisionStudents(
      groupId: $groupId
      contributionAmount: $contributionAmount
      currency: $currency
      documents: $documents
    ) {
      results {
        pandadocId
        success
        skipped
        agreementId
        error
      }
      errors
    }
  }
`;

/**
 * Queues a full PandaDoc re-sync: the backend re-pulls the document list,
 * marks student documents missing a folder for a details backfill and drains
 * the whole backlog itself over several passes. No arguments — the frontend
 * just triggers it and polls `pandadocSyncStatus` for progress.
 */
export const TriggerPandadocSync = gql`
  mutation RunPandaDocSync {
    triggerPandadocSync {
      enqueued
      message
      syncStatus {
        documentsCount
        detailsPending
        lastListSyncAt
        lastDetailsSyncAt
        newestDocDate
        lastDocumentsPulled
        lastRunStatus
        lastError
      }
    }
  }
`;

/**
 * Manually attaches a `candidate` PandaDoc document (surfaced by an email/phone
 * match) to an existing agreement. Operation name intentionally differs from the
 * backend field `linkPandadocDocument` to avoid codegen result-type collapse.
 * `errors` is a JSONString — null on success, otherwise a field→message object.
 */
export const LinkPandaDocDocument = gql`
  mutation LinkPandaDocDocument($pandadocId: String!, $agreementId: ID!) {
    linkPandadocDocument(pandadocId: $pandadocId, agreementId: $agreementId) {
      document {
        id
        pandadocId
        matchType
        agreement {
          id
          agreementTitle
        }
      }
      errors
    }
  }
`;

/**
 * Detaches a manually `linked` PandaDoc document from its agreement, turning it
 * back into a `candidate`. Provisioned links are immutable and rejected by the
 * backend. Operation name differs from the field `unlinkPandadocDocument`.
 */
export const UnlinkPandaDocDocument = gql`
  mutation UnlinkPandaDocDocument($pandadocId: String!) {
    unlinkPandadocDocument(pandadocId: $pandadocId) {
      document {
        id
        pandadocId
        matchType
        agreement {
          id
          agreementTitle
        }
      }
      errors
    }
  }
`;

/**
 * Teacher analog of LinkPandaDocDocument (spec 0007) — attaches a `candidate`
 * PandaDoc document to a teacher. Operation name differs from the backend field
 * `linkPandadocTeacher` to avoid codegen result-type collapse. `errors` is a
 * JSONString — null on success, otherwise a field→message object.
 */
export const LinkPandaDocTeacher = gql`
  mutation AttachPandadocTeacher($pandadocId: String!, $teacherId: ID!) {
    linkPandadocTeacher(pandadocId: $pandadocId, teacherId: $teacherId) {
      document {
        id
        pandadocId
        matchType
        teacher {
          id
        }
      }
      errors
    }
  }
`;

/**
 * Teacher analog of UnlinkPandaDocDocument — detaches a manually `linked`
 * document from its teacher. Operation name differs from the field
 * `unlinkPandadocTeacher`.
 */
export const UnlinkPandaDocTeacher = gql`
  mutation DetachPandadocTeacher($pandadocId: String!) {
    unlinkPandadocTeacher(pandadocId: $pandadocId) {
      document {
        id
        pandadocId
        matchType
        teacher {
          id
        }
      }
      errors
    }
  }
`;
